/**
 * The chat box's unsent words and its height (components/ChatThread.js; a staff request on /dashboard, 2026-11-03:
 * "PLEASE allow me to make this text box bigger especially when editing large messages", and "If I hit it right away I
 * lose whatever I was typing").
 *
 * THE DRAFT: each thread's unsent words are kept for the browser tab (sessionStorage), under its project and chat system,
 * so a reload or another chat opening in the same box never throws them away; sending clears them. Storage that is
 * missing or throws (a private window, blocked site data) keeps nothing and breaks nothing.
 *
 * THE HEIGHT: as typed, the box grows with its words up to GROW_MAX px, then scrolls.
 *
 * THE BIG BOX (its expand button; Ryan, 2026-11-03: "pop out or expand to the left past the drawer ... into a big gmail
 * sized window"): the words open in a window of their own, from under the top bar (TOP px) to near the bottom. Where the
 * drawer stands at the right (DRAWER_W px wide from DOCK_AT px: ProjectDrawer's `lg:w-[440px]`), the window stands to
 * its left, at most BIG_W px wide, so the chat stays in view beside it. On a narrower screen the drawer is the whole
 * screen and the window covers it, EDGE px in from every side.
 */
export const DRAFT_PREFIX = 'ug_chat_draft:';
export const GROW_MAX = 150;
export const DRAWER_W = 440;
export const DOCK_AT = 1024;
export const BIG_W = 960;
export const EDGE = 16;
export const TOP = 48;          // the drawer's 32 px top bar, then EDGE

export const draftKey = (projectId, system) => `${DRAFT_PREFIX}${projectId}:${system}`;

function tabStore() {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch { return null; }
}

/** The thread's kept words, or '' when there are none or storage cannot be read. */
export function readDraft(key, store = tabStore()) {
  try {
    const got = store ? store.getItem(key) : null;
    return typeof got === 'string' ? got : '';
  } catch { return ''; }
}

/** Keep the thread's words; nothing to keep (sent, or all deleted) takes the kept copy away. */
export function writeDraft(key, text, store = tabStore()) {
  try {
    if (!store) return;
    if (text) store.setItem(key, text);
    else store.removeItem(key);
  } catch { /* nothing kept: the box still works */ }
}

/** The box's height in px for words `scrollHeight` px tall. */
export const boxHeight = (scrollHeight) => Math.min(scrollHeight, GROW_MAX);

/** The big box's place (a fixed element's style, px) on a screen `viewW` px wide. */
export function popOutPlace(viewW) {
  if (viewW < DOCK_AT) return { top: TOP, bottom: EDGE, left: EDGE, right: EDGE };
  const right = DRAWER_W + EDGE;
  return { top: TOP, bottom: EDGE, right, width: Math.min(BIG_W, viewW - right - EDGE) };
}
